import React from 'react';
import { Box, Flex, IconButton, TextInput, useSnackbar } from 'pounceV2';
import dayjs from 'dayjs';
import { useEditAIInferenceStream } from 'Components/AIExplainLogEvents/graphql/editAiInferenceStream.generated';
import { useUpdateAIConversation } from 'Pages/AI/graphql/updateAIConversation.generated';
import { extractErrorMessage } from 'Helpers/utils';
import { AIInferenceStream, AISummarizeKind } from 'Generated/schema';
import { getTitleForKind } from 'Components/AIExplainLogEvents/utils/aiKindUtils';

export const getStreamName = (
  stream: Pick<AIInferenceStream, 'streamName' | 'streamId' | 'createdAt'>,
  kind: AISummarizeKind,
) => {
  if (stream?.streamName) {
    return stream.streamName;
  }
  if (stream?.streamId) {
    const id = stream.streamId.slice(9, 17);
    const date = dayjs(stream.createdAt).format('ddd, MMM D, h:mm:ss A');
    const typeLabel = getTitleForKind(kind);
    return `${typeLabel} ${id} - ${date}`;
  }

  return getTitleForKind(kind);
};

const EditableThreadNameV3 = ({
  stream,
  kind,
  conversationId,
  conversationTitle,
  onCancelEdit,
  size = 'medium',
}: {
  stream?: Pick<AIInferenceStream, 'streamName' | 'streamId' | 'createdAt'>;
  kind?: AISummarizeKind;
  conversationId?: string | null;
  conversationTitle?: string | null;
  onCancelEdit: () => void;
  size?: 'medium' | 'small';
}) => {
  const { pushSnackbar } = useSnackbar();
  const isConversational = Boolean(conversationId);

  const initialName = React.useMemo(() => {
    if (isConversational) {
      return conversationTitle || '';
    }
    if (stream && kind) {
      return getStreamName(stream, kind);
    }
    return '';
  }, [isConversational, conversationTitle, stream, kind]);

  const [newName, setNewName] = React.useState(initialName);

  React.useEffect(() => {
    setNewName(initialName);
  }, [initialName]);

  const [editAIInferenceStream, { loading: streamLoading }] = useEditAIInferenceStream();
  const [updateConversation, { loading: conversationLoading }] = useUpdateAIConversation();
  const loading = streamLoading || conversationLoading;

  const handleEditThreadName = React.useCallback(() => {
    if (isConversational && conversationId) {
      updateConversation({
        variables: { input: { conversationId, title: newName } },
        onCompleted: () => {
          pushSnackbar({ variant: 'success', title: 'Conversation renamed' });
          onCancelEdit();
        },
        onError: error => {
          pushSnackbar({
            variant: 'error',
            title: 'Failed to rename conversation',
            description: extractErrorMessage(error),
          });
        },
        refetchQueries: ['AIConversation', 'AIConversations'],
        awaitRefetchQueries: true,
      });
    } else if (stream) {
      editAIInferenceStream({
        variables: {
          input: { streamId: stream.streamId, streamName: newName },
        },
        onCompleted: () => {
          pushSnackbar({
            variant: 'success',
            title: 'Name updated',
          });
          onCancelEdit();
        },
        onError: error => {
          pushSnackbar({
            variant: 'error',
            title: 'Failed to update name',
            description: extractErrorMessage(error),
          });
        },
      });
    } else {
      pushSnackbar({ variant: 'error', title: 'Unable to rename: no target provided' });
      onCancelEdit();
    }
  }, [
    isConversational,
    conversationId,
    stream,
    updateConversation,
    editAIInferenceStream,
    pushSnackbar,
    newName,
    onCancelEdit,
  ]);

  const isValid = newName.length > 0 && newName !== initialName;

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && isValid) {
        handleEditThreadName();
      }
    },
    [handleEditThreadName, isValid],
  );

  return (
    <Flex alignItems="center" spacing={2} height={size === 'medium' ? 38 : undefined}>
      <Box width={size === 'medium' ? 340 : 300}>
        {size === 'medium' ? (
          <TextInput
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            label="Response name"
            onKeyDown={handleKeyDown}
          />
        ) : (
          <TextInput
            label=""
            autoFocus
            fullWidth
            hiddenLabel
            value={newName}
            onChange={e => setNewName(e.target.value)}
            size="small"
            onKeyDown={handleKeyDown}
          />
        )}
      </Box>
      <IconButton
        icon="check"
        size={size}
        disabled={!isValid || loading}
        variant="ghost"
        onClick={handleEditThreadName}
      />
      <IconButton icon="close" size={size} variant="ghost" onClick={() => onCancelEdit()} />
    </Flex>
  );
};

export default React.memo(EditableThreadNameV3);
